import React from 'react';
import { Box, Container, Typography, Grid, Card, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';

const OurWork = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    const platforms = [
        {
            title: 'Gym Owner',
            description: 'Streamline your gym operations with our AI-powered management system. From member tracking to class scheduling, we make running your fitness business easier.',
            icon: FitnessCenterIcon,
            color: '#2196f3'
        },
        {
            title: 'Athlete',
            description: 'Track your progress, set goals, and connect with trainers. Our platform helps you achieve your fitness goals with personalized workout plans and nutrition guidance.',
            icon: DirectionsRunIcon,
            color: '#f44336'
        },
        {
            title: 'Chef',
            description: 'Create and manage meal plans, track nutrition, and connect with clients. Our tools help you deliver personalized nutrition solutions efficiently.',
            icon: RestaurantIcon,
            color: '#4caf50'
        }
    ];

    const values = [
        {
            icon: RocketLaunchIcon,
            title: 'Innovation First',
            description: 'Pushing the boundaries of AI technology to create seamless fitness and lifestyle experiences.',
            color: '#2196f3'
        },
        {
            icon: LightbulbIcon,
            title: 'Smart Solutions',
            description: 'Developing intelligent systems that adapt and learn from user interactions.',
            color: '#ff9800'
        },
        {
            icon: GroupsIcon,
            title: 'Community Driven',
            description: 'Building a platform that connects and empowers fitness enthusiasts and professionals.',
            color: '#4caf50'
        }
    ];

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                {/* Hero Section */}
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 12 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Our Work
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        At Hubitable, we're revolutionizing the fitness and lifestyle industry through innovative AI solutions. Our platform connects gym owners, athletes, and chefs to create a seamless ecosystem for health and wellness.
                    </Typography>
                </Box>

                {/* Platforms Section */}
                <Box className="animate-in" sx={{ mb: 12 }}>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: 'center',
                            mb: 6,
                            color: 'text.primary'
                        }}
                    >
                        Our Platforms
                    </Typography>
                    <Grid container spacing={4}>
                        {platforms.map((platform) => {
                            const Icon = platform.icon;
                            return (
                                <Grid key={platform.title} xs={12} md={4}>
                                    <Card
                                        className="animate-in"
                                        variant="soft"
                                        sx={{
                                            height: '100%',
                                            textAlign: 'center',
                                            p: 4,
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 'md',
                                            },
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 48,
                                                mb: 2,
                                                color: platform.color
                                            }}
                                        />
                                        <Typography level="h4" mb={2}>
                                            {platform.title}
                                        </Typography>
                                        <Typography level="body2" sx={{ color: 'text.secondary' }}>
                                            {platform.description}
                                        </Typography>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                <Divider sx={{ my: 8 }} />

                {/* Values Section */}
                <Box className="animate-in" sx={{ mb: 12 }}>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: 'center',
                            mb: 6,
                            color: 'text.primary'
                        }}
                    >
                        Our Values
                    </Typography>
                    <Grid container spacing={4}>
                        {values.map((value) => {
                            const Icon = value.icon;
                            return (
                                <Grid key={value.title} xs={12} md={4}>
                                    <Card
                                        className="animate-in"
                                        variant="soft"
                                        sx={{
                                            height: '100%',
                                            textAlign: 'center',
                                            p: 4,
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 'md',
                                            },
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 48,
                                                mb: 2,
                                                color: value.color
                                            }}
                                        />
                                        <Typography level="h4" mb={2}>
                                            {value.title}
                                        </Typography>
                                        <Typography level="body2" sx={{ color: 'text.secondary' }}>
                                            {value.description}
                                        </Typography>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                {/* Join Us Section */}
                <Box 
                    className="animate-in"
                    sx={{ 
                        textAlign: 'center',
                        maxWidth: 900,
                        mx: 'auto',
                        p: 6,
                        borderRadius: 'xl',
                        bgcolor: 'background.level1'
                    }}
                >
                    <Typography level="h2" mb={4}>
                        Join Our Team
                    </Typography>
                    <Typography 
                        level="body1"
                        sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.8,
                            fontSize: 'lg'
                        }}
                    >
                        We're always looking for talented individuals who share our passion for innovation and health. Visit our careers page to explore opportunities and join us in shaping the future of fitness and wellness.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default OurWork; 